/**
 * Smart Banner API Integration
 * Handles communication with Strapi backend
 */

import { SmartBannerError } from "../error/SmartBannerError.js";

// API configuration
const DEFAULT_CONFIG = {
  timeout: 5000, // 5 seconds
  retries: 2,
  cacheTimeout: 3600000, // 1 hour in milliseconds
};

/**
 * Fetch banners from Strapi API (batch support)
 * @param {Array} propsArray - Array of banner properties
 * @returns {Promise<Array>} Array of banner data for each place
 */
export async function fetchBanners(propsArray) {
  if (!Array.isArray(propsArray) || propsArray.length === 0) {
    throw SmartBannerError.apiError(
      "Props array is required and must not be empty"
    );
  }

  try {
    // Generate cache key for this request
    const cacheKey = generateCacheKey(propsArray);

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Build API request data
    const apiRequest = buildBatchAPIRequest(propsArray);

    // Make API request with timeout
    const data = await fetchWithTimeout(
      apiRequest,
      DEFAULT_CONFIG.timeout,
      propsArray
    );

    // Cache the response
    setCachedData(cacheKey, data);

    return data;
  } catch (error) {
    if (error instanceof SmartBannerError) {
      throw error;
    }

    throw SmartBannerError.apiError(
      `Failed to fetch banners: ${error.message}`,
      {
        propsArray,
        originalError: error,
      }
    );
  }
}

/**
 * Build batch API request data for multiple places
 * @param {Array} propsArray - Array of banner properties
 * @returns {Object} API request data with URL and body
 */
export function buildBatchAPIRequest(propsArray) {
  // Use dynamic BFF proxy endpoint
  const baseUrl = window.SmartBanner?.getApiEndpoint();

  // Extract unique places
  const places = [...new Set(propsArray.map((props) => props.place))];

  // Get common properties for batch requests
  const firstProps = propsArray[0];

  // Collect all unique domains, pages, and environments from all props
  const allDomains = [
    ...new Set(propsArray.map((props) => props.domain).filter(Boolean)),
  ];
  const allPages = [
    ...new Set(
      propsArray
        .flatMap((props) => {
          // Support both 'page' (singular) and 'pages' (plural)
          const pageValue = props.pages || props.page;
          return Array.isArray(pageValue) ? pageValue : [pageValue];
        })
        .filter(Boolean)
    ),
  ];
  const allEnvs = [
    ...new Set(
      propsArray
        .flatMap((props) =>
          Array.isArray(props.env) ? props.env : [props.env]
        )
        .filter(Boolean)
    ),
  ];

  // Collect all unique inventories from all props
  const allInventories = [
    ...new Set(propsArray.map((props) => props.inventory).filter(Boolean)),
  ];

  // Build clean POST request body - only essential client data
  const requestBody = {
    // Banner criteria
    pages: allPages,
    domains: allDomains,
    environments: allEnvs,

    // NEW: Include inventory parameter
    inventory: allInventories.length === 1 ? allInventories[0] : undefined,

    // Version for compatibility
    version: firstProps.version || "1.0",

    // URL-based matching parameters
    currentPath:
      typeof window !== "undefined" ? window.location.pathname : undefined,
    currentHost:
      typeof window !== "undefined" ? window.location.hostname : undefined,

    // Device-based filtering - from data-smart-banner attribute
    isMobile:
      firstProps.isMobile !== undefined ? firstProps.isMobile : undefined,
  };

  return {
    url: `${baseUrl}/query`, // Use POST endpoint
    method: "POST",
    body: requestBody,
  };
}

/**
 * Fetch with timeout support using POST request
 * @param {Object} apiRequest - API request object with url, method, body
 * @param {number} timeout - Timeout in milliseconds
 * @param {Array} originalPropsArray - Original props array for transformation
 * @returns {Promise} Fetch response data
 */
async function fetchWithTimeout(apiRequest, timeout, originalPropsArray) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Prepare headers
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Cache-Control": "max-age=3600, public",
    };

    // Add preview mode header if running in Smart Banner Studio
    if (
      typeof window !== "undefined" &&
      window.SMART_BANNER_STUDIO_PREVIEW_MODE === true
    ) {
      headers["X-Studio-Preview-Mode"] = "true";
    }

    const response = await fetch(apiRequest.url, {
      method: apiRequest.method,
      signal: controller.signal,
      headers,
      body: JSON.stringify(apiRequest.body),
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    // Log API response for debugging
    // Only log in development/debug mode for security
    if (window.SmartBanner?.config?.debug) {
      console.log("[SmartBanner] API Response:", data);
      console.log("[SmartBanner] Request Body:", apiRequest.body);
    }

    // BFF now returns clean, ready-to-use data
    // No need for complex transformation - just group by place
    return groupBannersByPlace(data.data, originalPropsArray);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === "AbortError") {
      throw SmartBannerError.timeoutError(
        `Request timeout after ${timeout}ms`,
        { url: apiRequest.url }
      );
    }

    throw error;
  }
}

/**
 * Group clean banner data by place for rendering
 * @param {Array} banners - Clean banner data from BFF
 * @param {Array} propsArray - Original props array
 * @returns {Array} Banners grouped by place
 */
function groupBannersByPlace(banners, propsArray = []) {
  if (!banners || !Array.isArray(banners)) {
    return [];
  }

  // Group banners by position (place)
  const bannersByPlace = banners.reduce((acc, banner) => {
    const place = banner.position || 1;
    if (!acc[place]) {
      acc[place] = [];
    }

    // Apply environment filtering if needed
    const props =
      propsArray.find((p) => p.place === place) || propsArray[0] || {};
    const currentEnv = Array.isArray(props.env)
      ? props.env
      : [props.env || "DESKTOP_WEB"];

    // FIXED: Use new BFF response format
    acc[place].push({
      id: banner.id,
      place: place,
      // NEW: Use data from new BFF response format
      title: banner.title || "Smart Banner",
      bannerStatus: banner.bannerStatus,
      inventory: banner.inventory,
      images: banner.images || [], // Keep full images array from BFF
      pages: banner.pages,
      domains: banner.domains,
      startDate: banner.startDate,
      endDate: banner.endDate,
      campaigns: banner.campaigns,
      createdAt: banner.createdAt,
      updatedAt: banner.updatedAt,
      publishedAt: banner.publishedAt,
      // Legacy compatibility - extract from first image if needed
      url: banner.images?.[0]?.clickUrl,
      image: banner.images?.[0]?.img?.url,
      isActive: banner.bannerStatus === "ACTIVE",
      _raw: banner,
    });

    return acc;
  }, {});

  // Convert to array format expected by renderer
  return Object.keys(bannersByPlace)
    .sort((a, b) => Number(a) - Number(b))
    .map((place) => bannersByPlace[place]);
}

// DEPRECATED: transformStrapiResponse function removed - not used anywhere, only BFF API is used now

/**
 * Handle random image selection for search loading banners
 * @param {Array} imageArray - Array of images from banner
 * @param {boolean} isDomestic - Whether it's domestic search (optional)
 * @returns {Object|null} Selected image object
 */
function handleSearchLoadingBannerSelection(imageArray, isDomestic = true) {
  if (!imageArray || !Array.isArray(imageArray) || imageArray.length === 0) {
    return null;
  }

  // Random selection logic similar to handleFetchingImage
  const randomDigit = Math.floor(Math.random() * 100);

  // If images have threshold-based structure, use it
  // Otherwise, just pick a random image from array
  if (imageArray.length === 1) {
    return imageArray[0];
  }

  // Simple random selection from available images
  const randomIndex = Math.floor(Math.random() * imageArray.length);
  return imageArray[randomIndex];
}

/**
 * Extract URL from banner (from campaign relation or direct field)
 * @param {Object} banner - Banner data from Strapi
 * @returns {string|null} URL
 */
function extractUrlFromBanner(banner) {
  // Strapi 5 flattened format - direct field access
  if (banner.campaigns?.data?.length > 0) {
    const campaign = banner.campaigns.data[0];
    if (campaign.url) {
      return campaign.url;
    }
  }

  // Try array format (if campaigns is array)
  if (Array.isArray(banner.campaigns) && banner.campaigns.length > 0) {
    const campaign = banner.campaigns[0];
    if (campaign.url) {
      return campaign.url;
    }
  }

  // Fallback to direct URL field (if exists)
  if (banner.url) {
    return banner.url;
  }

  return null;
}

/**
 * Transform banner data to include campaign details for external use
 * @param {Object} banner - Banner data from Strapi
 * @returns {Object} Enhanced banner data with campaign info
 */
export function extractCampaignDetails(banner) {
  const campaignDetails = {
    campaign_id: null,
    name: null,
    description: null,
    campaign_link: null,
    rules: null,
    discounts: null,
    voucher: null,
    started_at: null,
    expired_at: null,
    is_active: false,
  };

  // Extract from campaign relation
  let campaign = null;
  if (banner.campaigns?.data?.length > 0) {
    campaign = banner.campaigns.data[0];
  } else if (Array.isArray(banner.campaigns) && banner.campaigns.length > 0) {
    campaign = banner.campaigns[0];
  }

  if (campaign) {
    campaignDetails.campaign_id = parseInt(campaign.campaignId) || campaign.id;
    campaignDetails.name = campaign.title;
    campaignDetails.description = campaign.description;
    campaignDetails.campaign_link = campaign.url;
    campaignDetails.rules = campaign.rules || [];
    campaignDetails.discounts = campaign.discounts || [];
    campaignDetails.voucher = campaign.voucher || null;
    campaignDetails.started_at = campaign.startDate;
    campaignDetails.expired_at = campaign.endDate;
    campaignDetails.is_active = campaign.isActive;
  }

  return campaignDetails;
}

/**
 * Extract image URL from Strapi media field
 * @param {Object|Array} images - Images field from Strapi
 * @returns {string|null} Image URL
 */
function extractImageUrl(images) {
  if (!images) return null;

  // Handle array format (Component repeatable) - Strapi 5 flattened
  if (Array.isArray(images) && images.length > 0) {
    const firstImage = images[0];

    // Try Strapi 5 flattened img field - şimdi populate edildi
    if (firstImage.img?.url) {
      return firstImage.img.url;
    }

    // Try img field with data wrapper
    if (firstImage.img?.data?.url) {
      return firstImage.img.data.url;
    }

    // Try nested attributes (legacy)
    if (firstImage.img?.data?.attributes?.url) {
      return firstImage.img.data.attributes.url;
    }

    // Try direct URL field in component
    if (firstImage.url) {
      return firstImage.url;
    }

    // Try data.attributes fallback
    if (firstImage.data?.attributes?.url) {
      return firstImage.data.attributes.url;
    }
  }

  // Handle direct media object
  if (images.data?.url) {
    return images.data.url;
  }

  // Handle legacy direct media object
  if (images.data?.attributes?.url) {
    return images.data.attributes.url;
  }

  // Handle simple URL string
  if (typeof images === "string") {
    return images;
  }

  return null;
}

/**
 * Generate cache key for banner request
 * @param {Array} propsArray - Banner properties array
 * @returns {string} Cache key
 */
function generateCacheKey(propsArray) {
  // Include current path for URL-based matching
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  // Create a stable key based on props
  const keyData = propsArray.map((props) => ({
    place: props.place,
    pages: Array.isArray(props.pages)
      ? props.pages.sort().join(",")
      : props.pages,
    domain: props.domain,
    size: props.size,
    env: Array.isArray(props.env) ? props.env.sort().join(",") : props.env,
    version: props.version || "default",
  }));

  const keyString = JSON.stringify({ keyData, currentPath });

  // Simple hash function for shorter keys
  let hash = 0;
  for (let i = 0; i < keyString.length; i++) {
    const char = keyString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return `sb_${Math.abs(hash)}`;
}

/**
 * Get cached data if available and not expired
 * @param {string} cacheKey - Cache key
 * @returns {Array|null} Cached data or null
 */
function getCachedData(cacheKey) {
  if (!window.SmartBanner?.cache) return null;

  const cached = window.SmartBanner.cache.get(cacheKey);
  if (!cached) return null;

  // Check if expired
  if (Date.now() - cached.timestamp > DEFAULT_CONFIG.cacheTimeout) {
    window.SmartBanner.cache.delete(cacheKey);
    return null;
  }

  return cached.data;
}

/**
 * Set cache data with timestamp
 * @param {string} cacheKey - Cache key
 * @param {Array} data - Data to cache
 */
function setCachedData(cacheKey, data) {
  if (!window.SmartBanner?.cache) return;

  window.SmartBanner.cache.set(cacheKey, {
    data,
    timestamp: Date.now(),
  });
}

/**
 * Clear expired cache entries
 */
export function clearExpiredCache() {
  if (!window.SmartBanner?.cache) return;

  const now = Date.now();
  for (const [key, value] of window.SmartBanner.cache.entries()) {
    if (now - value.timestamp > DEFAULT_CONFIG.cacheTimeout) {
      window.SmartBanner.cache.delete(key);
    }
  }
}
